//Global imports
import {
    CButton,
    CForm,
    CFormInput,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CFormSwitch,
} from '@coreui/react';
import { useState, useEffect } from 'react';

//File imports
import { Language } from '../../../../language/English';
import { DocumentTemplateService } from '../../Api/DocumentTemplateService';
import Notification from '../../../../services/Notification/Notification';
import Loader from '../../../../components/Layout/Loader';
import Logger from '../../../../services/Logger/Logger';

//Component imports
import { HintText, LengthChecker } from '../../../../components';
import CIcon from '@coreui/icons-react';
import { cilInfo } from '@coreui/icons';

interface CreateTemplateModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    mode?: 'create' | 'edit';
    templateData?:
        | {
              documentTemplateId?: number;
              name: string;
              price: number;
              outsidePrice: number;
              preparationTime: number;
          }
        | any;
}

interface FormErrors {
    name?: string;
    price?: string;
    outsidePrice?: string;
    preparationTime?: string;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
    visible,
    onClose,
    onSuccess = () => {},
    mode = 'create',
    templateData,
}) => {
    const notification = new Notification();
    const documentTemplateService = new DocumentTemplateService();
    const logger = new Logger();

    const [formData, setFormData] = useState({
        name: templateData?.name || '',
        price: templateData?.price.toString() || '',
        outsidePrice: templateData?.outsidePrice.toString() || '',
        preparationTime:
            templateData?.preparationTimeInMinutes.toString() || '',
        isInstructionsOnlyDocument: templateData?.isInstructionsOnlyDocument || false,
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [showLoader, setShowLoader] = useState<boolean>(false);

    useEffect(() => {
        if (templateData) {
            setFormData({
                name: templateData?.name,
                price: templateData?.price.toString(),
                outsidePrice: templateData?.outsidePrice.toString(),
                preparationTime:
                    templateData?.preparationTimeInMinutes.toString(),
                isInstructionsOnlyDocument: templateData?.isInstructionsOnlyDocument || false,
            });
        }
    }, [templateData]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        let isValid = true;

        if (!formData.name.trim()) {
            newErrors.name = Language.NAME_REQUIRED;
            isValid = false;
        }

        if (!formData.price) {
            newErrors.price = Language.PRICE_REQUIRED;
            isValid = false;
        } else if (
            isNaN(Number(formData.price)) ||
            Number(formData.price) <= 0
        ) {
            newErrors.price = Language.ENTER_VALID_PRICE;
            isValid = false;
        }

        if (!formData.outsidePrice) {
            newErrors.outsidePrice = Language.OUTSIDEPRICE_REQUIRED;
            isValid = false;
        } else if (
            isNaN(Number(formData.outsidePrice)) ||
            Number(formData.outsidePrice) <= 0
        ) {
            newErrors.outsidePrice = Language.ENTER_VALID_OUTSIDEPRICE;
            isValid = false;
        }

        if (!formData.preparationTime) {
            newErrors.preparationTime = Language.PREPARATION_TIME_REQUIRED;
            isValid = false;
        } else if (
            isNaN(Number(formData.preparationTime)) ||
            Number(formData.preparationTime) <= 0
        ) {
            newErrors.preparationTime = Language.ENTER_VALID_PREPARATION_TIME;
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowLoader(true);
        try {
            let success = false;
            let message = '';

            if (mode === 'create') {
                const response = await documentTemplateService.create(
                    formData.name,
                    Number(formData.price),
                    Number(formData.outsidePrice),
                    Number(formData.preparationTime),
                    formData.isInstructionsOnlyDocument,
                );
                success = response.success;
                message = response.message;
            } else if (mode === 'edit' && templateData?.documentTemplateId) {
                const response = await documentTemplateService.update(
                    templateData.documentTemplateId,
                    formData.name,
                    Number(formData.price),
                    Number(formData.outsidePrice),
                    Number(formData.preparationTime),
                    formData.isInstructionsOnlyDocument,
                );
                success = response.success;
                message = response.message;
            }

            if (success) {
                notification.success(
                    mode === 'create'
                        ? Language.DOCUMENT_ADDED_SUCCESSFULLY
                        : Language.DOCUMENT_UPDATED_SUCCESSFULLY,
                );
                setFormData({
                    name: '',
                    price: '',
                    outsidePrice: '',
                    preparationTime: '',
                    isInstructionsOnlyDocument: false,
                });
                onClose();
                onSuccess();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name as keyof FormErrors]) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    return (
        <CModal visible={visible} onClose={onClose}>
            {showLoader && <Loader />}
            <CModalHeader>
                <CModalTitle>
                    {mode === 'create'
                        ? Language.CREATE_DOCUMENT
                        : Language.EDIT_DOCUMENT}
                </CModalTitle>
            </CModalHeader>
            <CModalBody>
                <CForm onSubmit={handleSubmit}>
                    <div>
                        <CFormInput
                            type="text"
                            name={Language.NAME.toLowerCase()}
                            label={Language.NAME}
                            maxLength={100}
                            placeholder={Language.DOCUMENT_NAME_PLACEHOLDER}
                            value={formData.name}
                            onChange={handleChange}
                            invalid={!!errors.name}
                            feedback={errors.name}
                        />
                        <LengthChecker
                            currentLength={formData.name.length}
                            maxLength={100}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">
                            {Language.PRICE_IN_CHF}
                            <HintText>{Language.PRICE_HINT}</HintText>
                        </label>
                        <CFormInput
                            type="number"
                            name={Language.PRICE.toLowerCase()}
                            placeholder={Language.ENTER_PRICE}
                            value={formData.price}
                            onChange={handleChange}
                            invalid={!!errors.price}
                            feedback={errors.price}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">
                            {Language.OUTSIDE_PRICE_IN_CHF}
                            <HintText>{Language.OUTSIDE_PRICE_HINT}</HintText>
                        </label>
                        <CFormInput
                            type="number"
                            name={Language.OUTSIDE_PRICE.toLowerCase().replace(
                                / (.)/,
                                (_, c) => c.toUpperCase(),
                            )}
                            placeholder={Language.ENTER_OUTSIDE_PRICE}
                            value={formData.outsidePrice}
                            onChange={handleChange}
                            invalid={!!errors.outsidePrice}
                            feedback={errors.outsidePrice}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">
                            {Language.PREPARATION_TIME_MINUTES}
                            <HintText>
                                {Language.PREPARATION_TIME_HINT}
                            </HintText>
                        </label>
                        <CFormInput
                            type="number"
                            name={Language.PREPARATION_TIME}
                            placeholder={Language.ENTER_PREPARATION_TIME}
                            value={formData.preparationTime}
                            onChange={handleChange}
                            invalid={!!errors.preparationTime}
                            feedback={errors.preparationTime}
                        />
                    </div>
                    <div className="mb-3">
                        <CFormSwitch
                            label={Language.IS_INSTRUCTIONS_ONLY_GUIDE}
                            id="isInstructionsOnlyDocumentSwitch"
                            name="isInstructionsOnlyDocument"
                            checked={formData.isInstructionsOnlyDocument}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    isInstructionsOnlyDocument: e.target.checked,
                                }))
                            }
                        />
                        <HintText>{Language.IS_INSTRUCTIONS_ONLY_GUIDE_HINT}</HintText>
                    </div>
                </CForm>
            </CModalBody>
            <CModalFooter>
                <CButton color="secondary" onClick={onClose}>
                    {Language.CANCEL}
                </CButton>
                <CButton color="primary" onClick={handleSubmit}>
                    {mode === 'create'
                        ? Language.CREATE
                        : Language.UPDATE_BUTTON}
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

export default CreateTemplateModal;
